import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { type ConnectionBannerStatus } from 'app/shared/composables/use-connection-status-banner';

const MESSAGE_KEYS: Record<ConnectionBannerStatus, string> = {
    offline: 'NETWORK_STATUS_OFFLINE_MESSAGE',
    restored: 'NETWORK_STATUS_ONLINE_MESSAGE',
};

const BANNER_HEIGHT_CSS_VAR = '--connection-banner-height';

@Component({
    selector: 'app-connection-status-banner',
    imports: [TranslatePipe],
    templateUrl: './connection-status-banner.component.html',
    styleUrl: './connection-status-banner.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectionStatusBannerComponent {
    status = input<ConnectionBannerStatus | null>(null);

    protected readonly messageKey = computed(() => {
        const status = this.status();
        return status ? MESSAGE_KEYS[status] : null;
    });

    private readonly rootStyle = inject(DOCUMENT).documentElement.style;
    private readonly banner = viewChild<ElementRef<HTMLElement>>('banner');

    constructor() {
        effect((onCleanup) => {
            const banner = this.banner()?.nativeElement;

            if (!banner) {
                this.rootStyle.setProperty(BANNER_HEIGHT_CSS_VAR, '0px');
                return;
            }

            const publishHeight = () => this.rootStyle.setProperty(BANNER_HEIGHT_CSS_VAR, `${banner.offsetHeight}px`);
            publishHeight();

            const observer = new ResizeObserver(publishHeight);
            observer.observe(banner);

            onCleanup(() => {
                observer.disconnect();
                this.rootStyle.setProperty(BANNER_HEIGHT_CSS_VAR, '0px');
            });
        });
    }
}
